import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Col, Row } from 'react-bootstrap'
import { Field, reduxForm } from 'redux-form'
import { get, map, keys, includes } from 'lodash'

import { Input } from 'Components/Form/Input'
import { Dropzone } from 'Components/Form/Dropzone'
import SubmitButton from 'Components/Form/SubmitButton'
import { SectionLabel } from 'Components/Section/SectionLabel'
import { SectionTitle } from 'Components/Section/SectionTitle'
import { SelectDashboard } from 'Components/Form/SelectDashboard'
import { SectionWrapper } from 'Components/Section/SectionWrapper'
import { SubmitButtonBlock } from 'Components/Form/SubmitButton/SubmitButtonBlock'

import { required, zeroRequired, email } from 'Utils/Validators/formValidation'

import { getCities } from 'Reducers/City/actions'
import { getClients } from 'Reducers/Clients/actions'

import { USER_STATUSES } from 'Reducers/Users/const'
import { authorization } from 'Module/Auth/Auth'
import { CONFIG } from 'Config/config'

import { isClientRole } from 'Utils/Validators/roles'
import { validate } from './validate'
import { roleOptions } from 'Containers/UserManagement/UserDetails/utils'
import { Checkbox } from 'Components/Form/Checkbox'

const statusOptions = [
  {
    label: 'Active',
    value: USER_STATUSES.active,
  },
  {
    label: 'Blocked',
    value: USER_STATUSES.blocked,
  },
]

const setRoles = availableRoles =>
  map(roleOptions, (data, roleName) => ({
    ...data,
    disabled: !includes(availableRoles, roleName),
  }))

const roleOptionsByUserRole = {
  [CONFIG.USER_ROLE.ADMIN]: setRoles(keys(roleOptions)),
  [CONFIG.USER_ROLE.CITY_MANAGER]: setRoles([
    CONFIG.USER_ROLE.CITY_MANAGER,
    CONFIG.USER_ROLE.CLIENT,
  ]),
  [CONFIG.USER_ROLE.CLIENT_ADMIN]: setRoles([
    CONFIG.USER_ROLE.CLIENT_ADMIN,
    CONFIG.USER_ROLE.CLIENT,
  ]),
  [CONFIG.USER_ROLE.CLIENT]: setRoles([CONFIG.USER_ROLE.CLIENT]),
  [CONFIG.USER_ROLE.RECRUITER]: setRoles([CONFIG.USER_ROLE.RECRUITER]),
}

const UserManagementDetailsAddFormComponent = ({ handleSubmit }) => {
  const dispatch = useDispatch()

  const cities = useSelector(state =>
    state.city.citiesList.map(c => ({ value: c.id, label: c.name })),
  )

  const clients = useSelector(state =>
    state.clients.clientsList.map(c => ({ value: c.id, label: c.name })),
  )

  const selectedRole = useSelector(state =>
    get(state, 'form[user-details-add.values].role'),
  )

  const isNeedToShowSubroleForCityManager = useMemo(
    () => selectedRole === CONFIG.USER_ROLE.CITY_MANAGER,
    [selectedRole],
  )

  useEffect(() => {
    dispatch(getCities())
    dispatch(getClients())
  }, [])

  return (
    <form>
      <SectionWrapper className="section-wrapper first">
        <SectionTitle className="section-title">User Details</SectionTitle>
        <Col xs={12}>
          <Col xs={10}>
            <Row>
              <Col xs={3}>
                <SectionLabel className="section-label">
                  First Name
                </SectionLabel>
                <Field
                  component={Input}
                  id="firstName"
                  name="firstName"
                  validate={[required]}
                />
              </Col>
              <Col xs={3}>
                <SectionLabel className="section-label">Last Name</SectionLabel>
                <Field
                  component={Input}
                  id="lastName"
                  name="lastName"
                  validate={[required]}
                />
              </Col>
              <Col xs={3}>
                <SectionLabel className="section-label">
                  Mobile Phone
                </SectionLabel>
                <Field
                  component={Input}
                  id="mobilePhone"
                  name="mobilePhone"
                  validate={[required]}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={3}>
                <SectionLabel className="section-label">Password</SectionLabel>
                <Field
                  component={Input}
                  type="password"
                  id="password"
                  name="password"
                  validate={[required]}
                />
              </Col>
              <Col xs={3}>
                <SectionLabel className="section-label">
                  Repeat Password
                </SectionLabel>
                <Field
                  component={Input}
                  type="password"
                  id="repeatPassword"
                  name="repeatPassword"
                  validate={[required]}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={3}>
                <SectionLabel className="section-label">
                  Email Address
                </SectionLabel>
                <Field
                  component={Input}
                  id="email"
                  name="email"
                  validate={[required, email]}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={3}>
                <SectionLabel className="section-label">Role</SectionLabel>
                <Field
                  component={SelectDashboard}
                  name="role"
                  props={{
                    options:
                      roleOptionsByUserRole[authorization.getUser().role],
                    sorted: true,
                  }}
                  validate={[required]}
                />
              </Col>
              {isClientRole(selectedRole) ? (
                <Col xs={3}>
                  <SectionLabel className="section-label">Client</SectionLabel>
                  <Field
                    component={SelectDashboard}
                    id="client"
                    name="clientId"
                    props={{
                      options: clients,
                      sorted: true,
                    }}
                    validate={[required]}
                  />
                </Col>
              ) : (
                <Col xs={3}>
                  <SectionLabel className="section-label">City</SectionLabel>
                  <Field
                    component={SelectDashboard}
                    name="cityId"
                    props={{
                      options: cities,
                      sorted: true,
                    }}
                    validate={[required]}
                  />
                </Col>
              )}
              <Col xs={3}>
                <SectionLabel className="section-label">Status</SectionLabel>
                <Field
                  component={SelectDashboard}
                  name="status"
                  props={{
                    options: statusOptions,
                    sorted: true,
                  }}
                  validate={[zeroRequired]}
                />
              </Col>
            </Row>
            {isNeedToShowSubroleForCityManager ? (
              <Row>
                <Col xs={3}>
                  <SectionLabel className="section-label">
                    Is Assistant
                  </SectionLabel>
                  <Field component={Checkbox} name="isCityManagerAssistant" />
                </Col>
              </Row>
            ) : null}
          </Col>
          <Col xs={2}>
            <Col xs={3}>
              <Field
                component={Dropzone}
                id="profilePicture"
                name="profilePicture"
              />
            </Col>
          </Col>
        </Col>
      </SectionWrapper>
      <SubmitButtonBlock>
        <SubmitButton value="Save" onClick={handleSubmit} />
      </SubmitButtonBlock>
    </form>
  )
}

export const UserManagementDetailsAddForm = reduxForm({
  form: 'user-details-add',
  validate,
})(UserManagementDetailsAddFormComponent)
